/** Translate server-generated dealer portal copy by stable id/key. */

const CHECKLIST_IDS = new Set([
  "confirm_inventory",
  "add_photos",
  "respond_leads",
  "update_outcomes",
  "complete_profile",
  "review_quality",
  "upload_inventory",
]);

const ALERT_IDS = new Set([
  "needs_confirmation",
  "missing_photos",
  "missing_price_mileage",
  "low_quality",
  "sold_still_public",
  "recent_imports",
]);

const PROFILE_HEALTH_KEYS = new Set([
  "verified",
  "publicProfile",
  "logo",
  "cover",
  "description",
  "address",
  "whatsapp",
  "financing",
  "listings",
]);

const PLAN_WARNING_TYPES = new Set(["listing_limit", "lead_limit", "lead_limit_near"]);

const PLAN_DESCRIPTION_KEYS = new Set(["free", "starter", "growth", "premium", "custom"]);

function translateKey(t, key, params, fallback) {
  const value = t(key, params);
  if (!value || value === key || value.startsWith("[MISSING:")) {
    return fallback;
  }
  return value;
}

export function translateDealerChecklistItem(item, t) {
  if (!item?.id || !CHECKLIST_IDS.has(item.id)) {
    return item;
  }
  const key = `automotive.dealerPortal.server.checklist.${item.id}`;
  return {
    ...item,
    label: translateKey(t, key, { count: item.count }, item.label),
  };
}

export function translateDealerAlert(alert, t) {
  if (!alert?.id || !ALERT_IDS.has(alert.id)) {
    return alert;
  }
  const base = `automotive.dealerPortal.server.alerts.${alert.id}`;
  const count = alert.count ?? 0;
  return {
    ...alert,
    message: translateKey(t, `${base}.message`, { count }, alert.message),
    actionLabel: translateKey(t, `${base}.action`, { count }, alert.actionLabel),
  };
}

export function translateDealerProfileHealthItem(item, t) {
  if (!item?.key || !PROFILE_HEALTH_KEYS.has(item.key)) {
    return item;
  }
  if (item.key === "verified") {
    const key = item.complete
      ? "automotive.dealerPortal.server.profileHealth.items.verified"
      : "automotive.dealerPortal.server.profileHealth.items.verificationPending";
    return { ...item, label: translateKey(t, key, {}, item.label) };
  }
  const key = `automotive.dealerPortal.server.profileHealth.items.${item.key}`;
  return {
    ...item,
    label: translateKey(t, key, {}, item.label),
  };
}

export function translateDealerProfileHealthLabel(score, serverLabel, t) {
  if (score >= 85) {
    return translateKey(t, "automotive.dealerPortal.server.profileHealth.labels.excellent", {}, serverLabel);
  }
  if (score >= 70) {
    return translateKey(t, "automotive.dealerPortal.server.profileHealth.labels.good", {}, serverLabel);
  }
  if (score >= 50) {
    return translateKey(t, "automotive.dealerPortal.server.profileHealth.labels.needsWork", {}, serverLabel);
  }
  return translateKey(t, "automotive.dealerPortal.server.profileHealth.labels.incomplete", {}, serverLabel);
}

export function translateDealerTrialMessage(trialPanel, t) {
  if (!trialPanel?.trialStatus) {
    return trialPanel?.message || "";
  }
  const status = trialPanel.trialStatus;
  const key = `automotive.dealerPortal.server.trial.${status}`;
  const days = trialPanel.trialDaysRemaining;
  return translateKey(t, key, { days }, trialPanel.message);
}

export function translateDealerResponseTimePlaceholder(placeholder, t) {
  if (!placeholder) return null;
  return translateKey(
    t,
    "automotive.dealerPortal.server.responseTimePlaceholder",
    {},
    placeholder
  );
}

export function translateDealerDashboardPayload(dashboard, t) {
  if (!dashboard) return dashboard;

  return {
    ...dashboard,
    checklist: (dashboard.checklist || []).map((item) => translateDealerChecklistItem(item, t)),
    alerts: (dashboard.alerts || []).map((alert) => translateDealerAlert(alert, t)),
    profileHealth: dashboard.profileHealth
      ? {
          ...dashboard.profileHealth,
          label: translateDealerProfileHealthLabel(
            dashboard.profileHealth.score,
            dashboard.profileHealth.label,
            t
          ),
          items: (dashboard.profileHealth.items || []).map((item) =>
            translateDealerProfileHealthItem(item, t)
          ),
        }
      : dashboard.profileHealth,
    trialPanel: dashboard.trialPanel
      ? { ...dashboard.trialPanel, message: translateDealerTrialMessage(dashboard.trialPanel, t) }
      : dashboard.trialPanel,
    summaryCards: dashboard.summaryCards
      ? {
          ...dashboard.summaryCards,
          averageResponseTimePlaceholder: translateDealerResponseTimePlaceholder(
            dashboard.summaryCards.averageResponseTimePlaceholder,
            t
          ),
        }
      : dashboard.summaryCards,
  };
}

function translatePlanDescription(planKey, fallback, t) {
  if (!planKey || !PLAN_DESCRIPTION_KEYS.has(planKey)) {
    return fallback;
  }
  return translateKey(t, `automotive.dealerPortal.server.plan.descriptions.${planKey}`, {}, fallback);
}

function translatePlanPublicName(planKey, fallback, t) {
  if (!planKey) return fallback;
  return translateKey(t, `automotive.dealerPortal.plan.planLabels.${planKey}`, {}, fallback);
}

export function translateDealerPlanWarning(warning, t, usage = {}) {
  if (!warning?.type || !PLAN_WARNING_TYPES.has(warning.type)) {
    return warning;
  }
  return {
    ...warning,
    message: translateKey(
      t,
      `automotive.dealerPortal.server.plan.warnings.${warning.type}.message`,
      { listingLimit: usage.listingLimit ?? "—" },
      warning.message
    ),
  };
}

export function translateDealerFeaturedPayload(featured, t) {
  if (!featured) return featured;
  return {
    ...featured,
    upgradeMessage: featured.upgradeMessage
      ? translateKey(t, "automotive.dealerPortal.server.plan.featuredUpgrade", {}, featured.upgradeMessage)
      : featured.upgradeMessage,
    planPublicName: translatePlanPublicName(featured.planKey, featured.planPublicName, t),
  };
}

export function translateDealerPlanPayload(payload, t) {
  if (!payload) return payload;

  const usage = payload.usage || {};
  const plan = payload.plan || {};

  return {
    ...payload,
    plan: {
      ...plan,
      publicName: translatePlanPublicName(plan.planKey, plan.publicName, t),
      description: translatePlanDescription(plan.planKey, plan.description, t),
    },
    warnings: (payload.warnings || []).map((warning) => translateDealerPlanWarning(warning, t, usage)),
    comparisonPlans: (payload.comparisonPlans || []).map((entry) => ({
      ...entry,
      publicName: translatePlanPublicName(entry.planKey, entry.publicName, t),
      description: translatePlanDescription(entry.planKey, entry.description, t),
      priceLabel:
        entry.monthlyPriceCop != null
          ? null
          : translateKey(t, "automotive.dealerPortal.plan.priceContact", {}, entry.priceLabel),
    })),
  };
}

export function translateDealerApiError(
  message,
  t,
  fallbackKey = "automotive.dealerPortal.errors.generic",
  code,
  status
) {
  if (code) {
    const codeKey = `automotive.dealerPortal.errors.${code}`;
    const translated = t(codeKey);
    if (translated && translated !== codeKey && !translated.startsWith("[MISSING:")) {
      return translated;
    }
  }

  if (!code && status) {
    const statusKey = `automotive.dealerPortal.errors.http_${status}`;
    const translated = t(statusKey);
    if (translated && translated !== statusKey && !translated.startsWith("[MISSING:")) {
      return translated;
    }
  }

  if (!message) {
    return t(fallbackKey);
  }

  const normalized = String(message).trim().toLowerCase();
  const errorMap = [
    { match: "concesionario no encontrado", key: "automotive.dealerPortal.errors.dealerNotFound" },
    {
      match: "ya tienes una solicitud de upgrade pendiente",
      key: "automotive.dealerPortal.errors.upgrade_pending",
    },
    {
      match: "selecciona un plan superior",
      key: "automotive.dealerPortal.errors.upgrade_not_higher",
    },
    { match: "plan de destino inválido", key: "automotive.dealerPortal.errors.upgrade_invalid_plan" },
    {
      match: "no se pudo enviar la solicitud de upgrade",
      key: "automotive.dealerPortal.errors.submitUpgrade",
    },
    { match: "no se pudo cargar", key: fallbackKey },
    { match: "could not load", key: fallbackKey },
    { match: "unexpected error", key: "automotive.dealerPortal.errors.serverUnexpected" },
    { match: "midpoint api", key: "automotive.dealerPortal.errors.serverUnexpected" },
    { match: "failed to fetch", key: "automotive.dealerPortal.errors.network" },
    { match: "network", key: "automotive.dealerPortal.errors.network" },
    { match: "credenciales", key: "automotive.dealerPortal.login.errors.invalidCredentials" },
    { match: "invalid credentials", key: "automotive.dealerPortal.login.errors.invalidCredentials" },
  ];

  const match = errorMap.find(({ match: fragment }) => normalized.includes(fragment));
  if (match) {
    return t(match.key);
  }
  return message;
}
